import styles from './loginPage.module.css';
import { useContext, useState, useEffect, useRef } from "react";
import components from '../../resources/styles/components.module.css';
import { CLIENT_ID, CLIENT_SECRET, IAM_PATH, NONCE, OAUTH, OAUTH_AUTHORIZE, OAUTH_TOKEN, REDIRECT_URI, RESPONSE_TYPE, SCOPE, STATE } from "../../utils/Config";
import axios from "../../utils/api/axios";
import { AuthContext } from "../../utils/contextapi/AuthProvider";
import { useNavigate, useLocation } from "react-router-dom";
import InviteProfileSelectModal from "./modal/InviteProfileSelectModal";
import { Col } from "antd";
// import { DialogEnum, showDialog } from "../../utils/dialog/Dialog";

import { createPkceCodes } from "../../utils/pkce/pkceUtils"; // 경로는 실제 파일 위치에 맞게 조정
import { deleteCookie, setCookie } from "../../utils/cookies/cookies";
import { InnocodeButton, InnocodeInput, InnocodeTextButton, useDialog } from '@intsoft/innocode-core';


/**
 * 로그인 화면
 * @returns {JSX.Element}
 * @constructor
 */
function Login() {

    const { isAuthenticated, saveToken, saveAuthenticated, saveRefreshToken, saveTokenExpiresIn, saveRole, saveUser, doLogout } = useContext(AuthContext);
    const movePage = useNavigate();
    const { showDialog } = useDialog();
    const [isHiddenFail, setIsHiddenFail] = useState(true);
    const location = useLocation();
    const inviteState = location.state; // email, tenantId, inviter가 담겨 있음
    const wasAuthenticatedOnMount = useRef(isAuthenticated);
    const hasLoginAttempted = useRef(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteTenantId, setInviteTenantId] = useState(null);
    const [inviteTenantEmail, setInviteTenantEmail] = useState(null);

    // redirect_uri 상태 추가
    const [redirectUri, setRedirectUri] = useState(null);

    // 팝업 창 핸들러 상태
    const [popup, setPopup] = useState(null);

    const [inputs, setInputs] = useState({
        id: '',
        password: ''
    });

    useEffect(() => {
        if (inviteState?.tenantId && inviteState?.email) {
            setInputs((prev) => ({
                ...prev,
                id: inviteState.email
            }));
        }

        // [추가] URL 쿼리 파라미터에서 redirect_uri 값 가져오기
        const params = new URLSearchParams(location.search);
        const uri = params.get('redirect_uri');
        if (uri) {
            setRedirectUri(uri);
        }
    }, [inviteState, location.search]);

    useEffect(() => {
        // 로그인 직후에는 역할 조회가 끝난 뒤 handleLogin에서 이동합니다.
        // 여기서 먼저 이동하면 아직 비어 있는 role로 보호 라우트가 /unauthorized로 보낼 수 있습니다.
        if (!isAuthenticated || hasLoginAttempted.current) {
            return;
        }

        if (inviteState?.tenantId && inviteState?.email) {
            // 인증된 사용자가 초대 링크를 열었으면 초대 완료를 먼저 이어갑니다.
            // 방금 로그인한 경우에는 로그인 성공 처리에서 SSO 세션 준비 후 모달을 엽니다.
            if (wasAuthenticatedOnMount.current && !hasLoginAttempted.current) {
                setInviteTenantId(inviteState.tenantId);
                setInviteTenantEmail(inviteState.email);
                setShowInviteModal(true);
            }
            return;
        }

        movePage(redirectUri || '/account/manage', { replace: true });
    }, [isAuthenticated, movePage, redirectUri, inviteState]);

    const onChange = (e) => {
        const { value, name } = e.target;
        setInputs({
            ...inputs,
            [name]: value
        });
    };

    const handleEnter = (e) => {
        if (e.key === "Enter") {
            // onClickLogin();
        }
    };

    // 소셜 로그인 팝업을 열고 메시지를 수신하는 공통 로직
    const handleSocialLogin = async (provider) => {
        if (popup && !popup.closed) {
            popup.focus();
            return;
        }

        let socialUrl;

        if (provider === 'apple') {
            // Apple 인증 페이지 URL 생성
            const appleAuthConfig = {
                client_id: 'kr.intsoft.zentry', // Apple Developer에서 발급받은 Services ID
                redirect_uri: 'https://iam.innocode.co/oauth/apple/callback', // 백엔드의 /oauth/apple/callback 주소
                response_type: 'code', // code를 받기 위함
                scope: 'name email', // 이름과 이메일 요청
                response_mode: 'form_post', // 백엔드 콜백으로 POST 요청을 보내도록 설정
                state: 'STATE_STRING' // CSRF 방지를 위한 임의의 문자열
            };
            const appleAuthQueryString = new URLSearchParams(appleAuthConfig).toString();
            socialUrl = `https://appleid.apple.com/auth/authorize?${appleAuthQueryString}`;
        } else if (provider === 'spotify') {
            const spotifyAuthConfig = {
                client_id: '32b85776edaf48c1be1ed45305218bdc',
                response_type: 'code',
                redirect_uri: 'https://iam.innocode.co/oauth/spotify/callback',
                scope: 'user-read-private user-read-email',
                state: 'STATE_STRING' // CSRF 방지를 위한 임의의 문자열
            };
            const spotifyAuthQueryString = new URLSearchParams(spotifyAuthConfig).toString();
            socialUrl = `https://accounts.spotify.com/authorize?${spotifyAuthQueryString}`;
        } else if (provider === 'twitter') {
            socialUrl = `https://iam.innocode.co/oauth/twitter/login`;
        }
        else {
            // 기존 Google, Naver, Kakao 처리 방식
            socialUrl = `https://iam.innocode.co/oauth2/authorization/${provider}`;
        }

        const width = 500;
        const height = 600;
        const currentLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
        const currentTop = window.screenTop !== undefined ? window.screenTop : window.screenY;
        const currentWidth = window.innerWidth;
        const currentHeight = window.innerHeight;

        const popLeft = currentLeft + (currentWidth / 2) - (width / 2);
        const popTop = currentTop + (currentHeight / 2) - (height / 2);
        const features = `width=${width},height=${height},left=${popLeft},top=${popTop}`;

        const newPopup = window.open(socialUrl, `${provider}_oauth_popup`, features);

        if (!newPopup) {
            showDialog({
                iconType: "info",
                type: 'info',
                title: "알림",
                message: "팝업이 차단되었습니다. 브라우저 설정을 확인해주세요.",
                confirmText: "확인",
            });
            // showDialog(DialogEnum.DEFAULT, "알림", "팝업이 차단되었습니다. 브라우저 설정을 확인해주세요.");
            return;
        }

        setPopup(newPopup);

        const messageHandler = (event) => {
            if (event.origin !== 'https://iam.innocode.co') { // Origin 검사
                return;
            }
            const data = event.data;
            if (data.type === "social_login") {
                checkLinkedAccount(data);
                window.removeEventListener("message", messageHandler);
            }
        };

        window.addEventListener("message", messageHandler);

        // // 팝업 닫힘 감지
        // const checkPopupClosed = setInterval(() => {
        //     if (newPopup.closed) {
        //         clearInterval(checkPopupClosed);
        //         window.removeEventListener("message", messageHandler);
        //     }
        // }, 500);
    };

    const checkLinkedAccount = async (socialData) => {
        try {
            const existUrl = `${IAM_PATH}/exist/linkedacounts`;
            const existData = {
                linkedAcounts: {
                    [socialData.provider]: {
                        oauth_id: socialData.auth_id,
                    }
                }
            };

            const existRes = await axios.post(existUrl, existData);

            if (existRes.data.data) {
                const { unifiedProfileId } = existRes.data.data;
                // 연동된 계정이 있다면 바로 로그인
                handleLogin(unifiedProfileId, socialData.auth_id, socialData.provider);
            } else {
                // 연동된 계정이 없다면 회원가입 페이지로 이동
                movePage('/register', { state: { socialData, sigupType: 'social' } });
            }
        } catch (error) {
            console.error("existLinkedAcounts 실패:", error);
            showDialog(DialogEnum.DEFAULT, "오류", "계정 연동 확인에 실패했습니다.");
        }
    };

    async function getPublicIp() {
        try {
            const response = await fetch('https://api.ipify.org?format=json');
            const data = await response.json();
            return data.ip;
        } catch (error) {
            console.error('Failed to get public IP:', error);
            return null;
        }
    }

    async function bootstrapSsoSession() {
        try {
            await axios.post(`${OAUTH}/sso/session/bootstrap`, {}, { withCredentials: true });
        } catch (error) {
            console.error('SSO session bootstrap failed:', error);
        }
    }

    const handleLogin = async (id, password, loginType) => {
        hasLoginAttempted.current = true;
        saveToken("");
        saveTokenExpiresIn(null);
        saveRefreshToken("");
        saveAuthenticated(false);
        saveRole([]); // 역할은 보통 배열이므로 빈 배열로 초기화
        saveUser(null); // 사용자 정보는 null로 초기화
        deleteCookie('user_id');

        if (loginType === 'login') {
            if (!inputs.id || !inputs.password) {
                showDialog({
                    iconType: "info",
                    type: 'info',
                    title: "이메일 아이디 또는\n비밀번호를 입력해주세요.",
                    confirmText: "확인",
                });
                // setIsHiddenFail(false);
            } else {
                // setIsHiddenFail(true);
            }
        }
        const publicIp = await getPublicIp();

        try {
            fetch(IAM_PATH + '/mfa/status/' + id, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                }
            })
                .then(res => res.json())
                .then(async data => {
                    const status = data.data;
                    // const status = false;
                    // if (status) {
                    //     movePage('/mfa-auth', { state: { id: id, pw: password, loginType: loginType, loginToken: loginToken } });
                    // } else {
                    sessionStorage.clear();
                    const loginData = { unifiedProfileId: id, password: password, type: loginType }
                    await axios.post(IAM_PATH + "/login", loginData, { withCredentials: true })
                        .then(async response => {
                            console.log("response :::", response);
                            if (response.data.code === 400) {
                                showDialog({
                                    iconType: "info",
                                    type: 'info',
                                    title: "이메일 아이디 또는\n비밀번호를 확인해주세요.",
                                    confirmText: "확인",
                                });
                                return;
                            }

                            const loginToken = response.data.data.login_token;

                            if (status) {
                                movePage('/mfa-auth', {
                                    state: {
                                        id: id,
                                        pw: password,
                                        loginType: loginType,
                                        loginToken: loginToken,
                                        email: inviteState?.email,
                                        tenantId: inviteState?.tenantId,
                                        inviter: inviteState?.inviter,
                                    }
                                });
                                return;
                            }

                            const authorizeConfig = {
                                response_type: RESPONSE_TYPE,
                                client_id: CLIENT_ID,
                                redirect_uri: REDIRECT_URI,
                                scope: SCOPE,
                                state: STATE,
                                nonce: NONCE,
                                clientIp: publicIp
                            };
                            const authorizeQueryString = new URLSearchParams(authorizeConfig).toString();

                            await axios.get(`${OAUTH_AUTHORIZE}?${authorizeQueryString}`, {
                                headers: {
                                    Authorization: `Bearer ${loginToken}` // 일반적으로 login_token은 Authorization 헤더에 담음
                                },
                                withCredentials: true // 필요한 경우 true
                            })
                                .then(async response => {
                                    const authorization_code = response.data.code;
                                    const tokenConfig = {
                                        grant_type: "authorization_code",
                                        code: authorization_code,
                                        client_id: CLIENT_ID,
                                        client_secret: CLIENT_SECRET,
                                        redirect_uri: REDIRECT_URI,
                                        scope: SCOPE,
                                        state: STATE,
                                        code_verifier: NONCE,
                                    };
                                    const tokenQueryString = new URLSearchParams(tokenConfig).toString();

                                    await axios.post(`${OAUTH_TOKEN}?${tokenQueryString}`, null, { withCredentials: true })
                                        .then(async response => {
                                            setIsHiddenFail(true);
                                            const { access_token, expires_in, id_token, refresh_token, exp_timestamp, refresh_token_expires_in } = response.data;

                                            let finalExpiresIn;
                                            if (exp_timestamp) {
                                                // 서버에서 정확한 타임스탬프를 줬으므로 그대로 사용 (밀리초 단위로 가정)
                                                finalExpiresIn = exp_timestamp;
                                            } else {
                                                // 혹시 서버에서 주지 않는 경우를 위한 fallback (기존 방식)
                                                finalExpiresIn = Date.now() + (expires_in * 1000);
                                            }

                                            saveTokenExpiresIn(finalExpiresIn);
                                            saveToken(access_token, finalExpiresIn);
                                            saveRefreshToken(refresh_token, refresh_token_expires_in);
                                            saveAuthenticated(true);

                                            // 크롬이 완전히 닫혔다가 열렸을 경우, 해당 값을 토대로 재로그인 기능 구현.
                                            // sessionStorage.setItem("access_token", access_token);
                                            // sessionStorage.setItem("refresh_token", refresh_token);
                                            // sessionStorage.setItem("tokenExpiresIn", finalExpiresIn);
                                            setCookie("user_id", id, finalExpiresIn);
                                            await bootstrapSsoSession();

                                            await axios.get(OAUTH + "/userinfo")
                                                .then(userRes => {
                                                    const { data } = userRes;

                                                    // roles가 배열이면 그대로, 아니면 배열로 변환
                                                    const roles = Array.isArray(data.roles) ? data.roles.filter(Boolean) : [];

                                                    saveRole(roles.length > 0 ? roles : ["USER"], finalExpiresIn); // context에 저장
                                                    saveUser(data, finalExpiresIn);

                                                    // sessionStorage.setItem("roles", JSON.stringify(roles));
                                                    // sessionStorage.setItem("user", JSON.stringify(data));
                                                    // saveAuthenticated(true);

                                                    if (inviteState?.tenantId && inviteState?.email) {
                                                        // 초대 기반 테넌트 가입
                                                        // 여기서 프로필 선택 창이 뜨고 선택한 프로필로 테넌트 가입 이 필요
                                                        // 프로필 선택
                                                        setInviteTenantId(inviteState.tenantId);
                                                        setInviteTenantEmail(inviteState.email);
                                                        setShowInviteModal(true);
                                                    } else {
                                                        // [변경] redirectUri가 있으면 해당 경로로 이동, 없으면 기본 경로로 이동
                                                        const path = redirectUri || '/account/manage';
                                                        movePage(path);
                                                    }
                                                })
                                        })
                                        .catch(error => {
                                            console.error('OAUTH_TOKEN', error);
                                        });
                                })
                                .catch(error => {
                                    console.error('OAUTH_AUTHORIZE', error);
                                });
                        })
                        .catch(error => {
                            console.error(error);
                            setIsHiddenFail(false);
                        });
                    // }
                });
        } catch (error) {
            console.error("로그인 실패:", error);
            setIsHiddenFail(false);
            showDialog(DialogEnum.DEFAULT, "실패", "로그인에 실패했습니다.");
        }
    };

    const onClickLogin = () => {
        handleLogin(inputs.id, inputs.password, 'login');
    };

    const onClickRegist = () => {
        movePage('/register'); // 일반 로그인
    }

    const onClickFindId = () => {
        movePage('/findid')
    }

    const onClickFindPw = () => {
        movePage('/findpw');
    }

    const handleSubmit = (e) => {
        e.preventDefault(); // form의 기본 제출 동작(새로고침) 방지
        handleLogin(inputs.id, inputs.password, 'login');
        // onClickLogin();
    };

    return (
        <>
            {showInviteModal && (
                <InviteProfileSelectModal
                    tenantId={inviteTenantId}
                    unifiedProfileId={inviteTenantEmail}
                    onComplete={() => {
                        setShowInviteModal(false);
                        movePage('/account/manage');
                    }}
                />
            )}

            <div className={styles.innocode_breakpoint_layout_bg}>
                <div className={styles.innocode_breakpoint_content}>
                    <div className={styles.layout_container}>
                        <div className={styles.content_wrapper}>
                            <div className={styles.content_section}>
                                <div className={styles.login_section_inner}>
                                    <form onSubmit={handleSubmit}>
                                        <div className={styles.login_title}>Zentry</div>
                                        <div className={styles.login_form_group}>
                                            <div
                                                data-caption="false"
                                                data-error="false"
                                                data-essentialmark="true"
                                                data-info="false"
                                                data-label="true"
                                                data-successed="false"
                                                className={styles.login_form_row}
                                            >
                                                <div className={styles.login_form_label_row}>
                                                    <div className={styles.login_form_label}>Email</div>
                                                </div>
                                                <div className={styles.input_container}>
                                                    {/* <input
                                                        type="email"
                                                        id="id"
                                                        name="id"
                                                        placeholder="아이디"
                                                        value={inputs.id}
                                                        className={`${components.innocode_input} ${components.innocode_input_md}`}
                                                        onChange={onChange}
                                                        onKeyDown={handleEnter}
                                                        required
                                                    /> */}
                                                    <InnocodeInput
                                                        type="email"
                                                        id="id"
                                                        name="id"
                                                        placeholder="아이디"
                                                        value={inputs.id}
                                                        onChange={onChange}
                                                        onKeyDown={handleEnter}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                data-caption="false"
                                                data-error="false"
                                                data-essentialmark="true"
                                                data-info="false"
                                                data-label="true"
                                                data-successed="false"
                                                className={styles.login_form_row}
                                            >
                                                <div className={styles.login_form_label_row}>
                                                    <div className={styles.login_form_label}>Password</div>
                                                </div>
                                                {/* <input
                                                    type="password"
                                                    id="password"
                                                    name="password"
                                                    placeholder="********"
                                                    value={inputs.password}
                                                    className={`${components.innocode_input} ${components.innocode_input_md}`}
                                                    onChange={onChange}
                                                    onKeyDown={handleEnter}
                                                /> */}
                                                <InnocodeInput
                                                    type="password"
                                                    id="password"
                                                    name="password"
                                                    placeholder="********"
                                                    value={inputs.password}
                                                    onChange={onChange}
                                                    onKeyDown={handleEnter}
                                                />
                                            </div>
                                        </div>
                                        {/* {!isHiddenFail &&
                                            <div className={styles.feedback_text}>이메일 아이디 또는 패스워드를 확인해주세요.</div>
                                        } */}

                                        <div className={styles.login_submit_btn_container} >
                                            <InnocodeButton type="submit" size={"lg"} className={styles.login_submit_btn} >
                                                로그인
                                            </InnocodeButton>


                                            {/* <button
                                                type="submit"
                                                className={`${components.btn} ${components.btn_solid} ${components.btn_primary} ${components.btn_lg} ${styles.login_submit_btn}`}
                                            // className={`btn btn-solid btn-primary btn-lg login-submit-btn`}
                                            // onClick={onClickLogin}
                                            >
                                                <span className={styles.btn__text}>로그인</span>
                                            </button> */}
                                        </div>
                                    </form>
                                    <div className={styles.divider} />
                                    <div className={styles.social_login_group}>
                                        <a href="#" title="카카오로 로그인" className={styles.social_login_btn} onClick={() => handleSocialLogin('kakao')}>
                                            <img
                                                src="/assets/kakao-login.png"
                                                alt="카카오 로그인"
                                                className={styles.social_login_img}
                                            />
                                        </a>
                                        <a href="#" title="Google로 로그인" className={styles.social_login_btn} onClick={() => handleSocialLogin('google')}>
                                            <img
                                                src="/assets/google-login.png"
                                                alt="Google 로그인"
                                                className={styles.social_login_img}
                                            />
                                        </a>
                                        <a href="#" title="네이버로 로그인" className={styles.social_login_btn} onClick={() => handleSocialLogin('naver')}>
                                            <img
                                                src="/assets/naver-login.png"
                                                alt="네이버 로그인"
                                                className={styles.social_login_img}
                                            />
                                        </a>
                                        <a href="#" title="Apple로 로그인" className={styles.social_login_btn} onClick={() => handleSocialLogin('apple')}>
                                            <img
                                                src="/assets/apple-login.png"
                                                alt="Apple 로그인"
                                                className={styles.social_login_img}
                                            />
                                        </a>
                                        <a href="#" title="Facebook으로 로그인" className={styles.social_login_btn} onClick={() => handleSocialLogin('facebook')}>
                                            <img
                                                src="/assets/Facebook_Logo.png" // 페이스북 로고 이미지
                                                alt="Facebook 로그인"
                                                className={styles.social_login_img}
                                            />
                                        </a>
                                        <a href="#" title="Spotify으로 로그인" className={styles.social_login_btn} onClick={() => handleSocialLogin('spotify')}>
                                            <img
                                                src="/assets/Spotify_Primary_Logo_RGB_Green.png" // 페이스북 로고 이미지
                                                alt="Spotify 로그인"
                                                className={styles.social_login_img}
                                            />
                                        </a>
                                        <a href="#" title="Twitter으로 로그인" className={styles.social_login_btn} onClick={() => handleSocialLogin('twitter')}>
                                            <img
                                                src="/assets/icons8-twitter-x-logo.png" // 페이스북 로고 이미지
                                                alt="Twitter 로그인"
                                                className={styles.social_login_img}
                                            />
                                        </a>
                                    </div>
                                    <div className={styles.login_link_group}>
                                        <div className={styles.login_link_row}>
                                            <InnocodeTextButton
                                                type={"assistive"}
                                                onClick={onClickFindId}
                                            >
                                                ID 찾기
                                            </InnocodeTextButton>
                                            <InnocodeTextButton
                                                type={"assistive"}
                                                onClick={onClickFindPw}
                                            >
                                                비밀번호 찾기
                                            </InnocodeTextButton>
                                            {/* <div className={styles.login_link_btn} onClick={onClickFindId} >
                                                <div className={`${components.link_btn} ${components.link_btn_assistive}`} >
                                                    ID 찾기
                                                </div>
                                            </div> */}
                                            {/* <div className={styles.login_link_btn} onClick={onClickFindPw}>
                                                <div className={`${components.link_btn} ${components.link_btn_assistive}`} >
                                                    비밀번호 찾기
                                                </div>
                                            </div> */}
                                        </div>
                                        <div className={styles.login_link_btn}>
                                            <p className={components.link_btn_assistive}>아직 회원이 아니신가요?</p>
                                            <InnocodeTextButton
                                                type={"assistive"}
                                                onClick={onClickRegist}
                                            >
                                                회원가입
                                            </InnocodeTextButton>
                                            {/* <div className={`${components.link_btn} ${components.link_btn_primary}`}>
                                                회원가입
                                            </div> */}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </>
    )

}

export default Login;
